import { createContext, useContext, useId, type HTMLAttributes, type ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { cx } from "./cx";
import { Icon } from "./Icon";
import { Chevron } from "./Popover";

export function Shell({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cx("ax-shell", className)} {...rest} />;
}

/** 侧栏是否收成图标条，Sidebar 提供给 NavGroup 与 NavItem。 */
const RailContext = createContext(false);

export interface SidebarProps {
  brand: ReactNode;
  /** 收成图标条时品牌区显示的内容，缺省沿用 brand。 */
  brandCollapsed?: ReactNode;
  children: ReactNode;
  foot?: ReactNode;
  /** 收成只显示图标的窄条（宽 sidebar-w-collapsed）。 */
  collapsed?: boolean;
  /** 给了才在底部显示"收起菜单"按钮。 */
  onCollapsedChange?: (collapsed: boolean) => void;
}

/** 左侧导航：品牌区、导航、底部的收起按钮；collapsed 时收成图标条。 */
export function Sidebar({ brand, brandCollapsed, children, foot, collapsed = false, onCollapsedChange }: SidebarProps) {
  const toggleLabel = collapsed ? "展开菜单" : "收起菜单";
  const navId = useId();
  return (
    <aside className={cx("ax-sidebar", collapsed && "is-collapsed")}>
      <div className="ax-sidebar-brand">{collapsed ? brandCollapsed ?? brand : brand}</div>
      <nav id={navId} className="ax-nav" aria-label="主导航">
        <RailContext.Provider value={collapsed}>{children}</RailContext.Provider>
      </nav>
      {(foot || onCollapsedChange) && (
        <div className="ax-sidebar-foot">
          {!collapsed && foot}
          {onCollapsedChange && (
            <button type="button" className="ax-sidebar-toggle" aria-expanded={!collapsed} aria-controls={navId} aria-label={collapsed ? toggleLabel : undefined} title={collapsed ? toggleLabel : undefined} onClick={() => onCollapsedChange(!collapsed)}>
              <Icon>
                <rect width="18" height="18" x="3" y="3" rx="2" />
                <path d="M9 3v18" />
                <path d={collapsed ? "m14 9 3 3-3 3" : "m16 15-3-3 3-3"} />
              </Icon>
              {!collapsed && toggleLabel}
            </button>
          )}
        </div>
      )}
    </aside>
  );
}

/** 分组标题。给了 onToggle 就是可折叠的按钮，open 控制箭头朝向；图标条里换成一条分隔线。 */
export function NavGroup({ children, open = true, onToggle }: { children: ReactNode; open?: boolean; onToggle?: () => void }) {
  if (useContext(RailContext)) return <div className="ax-nav-sep" role="separator" />;
  if (!onToggle) return <div className="ax-nav-group">{children}</div>;
  return (
    <button type="button" className="ax-nav-group" aria-expanded={open} onClick={onToggle}>
      {children}
      <Chevron />
    </button>
  );
}

/** 导航项。count 显示需要关注的数量，例如熔断中的通道数；图标条里只显示图标，名称放进 title 与 aria-label。 */
export function NavItem({ to, icon, label, count }: { to: string; icon?: ReactNode; label: string; count?: number }) {
  const rail = useContext(RailContext);
  // 图标条里 aria-label 会盖掉计数的文字，所以把计数拼进名称
  const name = count != null && count > 0 ? `${label} ${count}` : label;
  return (
    <NavLink to={to} className={({ isActive }) => cx("ax-nav-item", isActive && "is-active")} title={rail ? name : undefined} aria-label={rail ? name : undefined}>
      {icon}
      {!rail && label}
      {count != null && count > 0 && <span className="ax-count">{count}</span>}
    </NavLink>
  );
}

export function Topbar({ crumbs, right }: { crumbs: ReactNode; right: ReactNode }) {
  return (
    <header className="ax-topbar">
      <nav className="ax-crumbs" aria-label="面包屑">{crumbs}</nav>
      <div className="ax-topbar-right">{right}</div>
    </header>
  );
}

export function Crumbs({ items }: { items: ReactNode[] }) {
  return (
    <>
      {items.map((it, i) => (
        <span key={i} className={i === items.length - 1 ? "current" : undefined}>
          {i > 0 && <span className="sep" aria-hidden> / </span>}
          {it}
        </span>
      ))}
    </>
  );
}

export function Main({ className, ...rest }: HTMLAttributes<HTMLElement>) {
  return <main className={cx("ax-main", className)} {...rest} />;
}
